import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ConfigContext } from "../Context/ConfigContext";
import { formatSlug } from "../Utils/Helper";

const Videos = () => {
    const { apiURL, apiHeaderJson } = useContext(ConfigContext);
    const [activeVideo, setActiveVideo] = useState(null);
    const [keyword, setKeyword] = useState("");
    const [videos, setVideos] = useState([]);
    const [page, setPage] = useState(1);
    const [next, setNext] = useState(false);
    const [prev, setPrev] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [limit] = useState(10);
    const [loading, setLoading] = useState(false);

    const getYouTubeId = (url) => {
        try {
            const parsed = new URL(url);
            if (parsed.hostname === "youtu.be") {
                return parsed.pathname.slice(1);
            } else if (parsed.hostname.includes("youtube.com")) {
                const params = parsed.searchParams;
                if (params.has("v")) return params.get("v");
                if (parsed.pathname.startsWith("/embed/")) return parsed.pathname.split("/embed/")[1];
                if (parsed.pathname.startsWith("/shorts/")) return parsed.pathname.split("/shorts/")[1];
            }
            return null;
        } catch {
            console.error("Invalid YouTube URL:", url);
            return null;
        }
    };

    const getData = async () => {
        try {
            setLoading(true);
            const headers = apiHeaderJson;
            const response = await axios.get(`${apiURL}Products/GetProductVideos`, {
                headers,
                params: {
                    page: page,
                    limit: limit,
                    keyword: keyword.trim()
                }
            });

            const { data, success, next, prev, total_pages, total_records } = response.data;

            if (success) {
                setVideos(data);
                setNext(next);
                setPrev(prev);
                setTotalPages(total_pages);
                setTotalRecords(total_records);
            }
        } catch (error) {
            console.error("Error fetching videos:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (video) => setActiveVideo(video);
    const handleCloseModal = () => setActiveVideo(null);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            getData();
        }, 500);

        return () => clearTimeout(timer);
    }, [keyword]);

    useEffect(() => {
        getData();
    }, [page]);

    return (
        <>
            <main className="main pages">
                <div className="page-header breadcrumb-wrap">
                    <div className="container">
                        <div className="breadcrumb">
                            <Link to="/" rel="nofollow">
                                <i className="fi-rs-home mr-5" /> Home
                            </Link>
                            <span /> Pages <span /> Videos
                        </div>
                    </div>
                </div>

                <div className="page-content pt-50 pb-50">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="position-relative" style={{ width: "100%", maxWidth: "400px" }}>
                                <input
                                    type="text"
                                    placeholder="Search videos..."
                                    name="keyword"
                                    value={keyword}
                                    onChange={(e) => {
                                        setPage(1);
                                        setKeyword(e.target.value);
                                    }}
                                    className="form-control ps-4 pe-5"
                                    style={{
                                        border: "1px solid #B76E79",
                                        borderRadius: "50px",
                                        height: "45px"
                                    }}
                                />
                                <i
                                    className="fa fa-search fs-5 position-absolute top-50 end-0 translate-middle-y pe-4 text-secondary"
                                    style={{ pointerEvents: "none" }}
                                />
                            </div>

                        </div>

                        {loading ? (
                            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4">
                                {[...Array(8)].map((_, i) => (
                                    <div className="col" key={i}>
                                        <div className="shimmer-card mb-2"></div>
                                        <div className="shimmer-line text-center" style={{ width: "50%", height: "16px", marginTop: "6px" }}></div>
                                    </div>
                                ))}
                            </div>
                        ) : videos.length === 0 ? (
                            <div className="text-center my-5 py-5">
                                <div className="d-inline-block p-4 rounded-3 bg-light">
                                    <i className="fas fa-video-slash fs-1 text-muted mb-3"></i>
                                    <h4 className="mb-3">No videos found</h4>
                                    {keyword && (
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                                setKeyword("");
                                                setPage(1);
                                            }}
                                        >
                                            Clear search
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="row">
                                    {videos.map((video, index) => {
                                        const videoId = getYouTubeId(video.product_video_url);
                                        const thumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                        return (
                                            <div className="col-md-3 mb-4" key={index}>
                                                <div className="card h-100 video-card border-0 rounded-4 overflow-hidden shadow-sm">
                                                    <div
                                                        className="position-relative"
                                                        onClick={() => handleOpenModal(video)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <img
                                                            src={thumbnail}
                                                            className="card-img-top"
                                                            alt={video.product_title}
                                                            style={{ aspectRatio: "16/9", objectFit: "cover" }}
                                                        />
                                                        <div className="play-button-overlay d-flex justify-content-center align-items-center">
                                                            <i className="fa fa-play-circle fa-4x text-white"></i>
                                                        </div>
                                                    </div>
                                                    <div className="card-body p-2 text-center">
                                                        <Link to={`/ProductDetails/${formatSlug(video.product_title)}=${video.product_id}`}>
                                                            <h5 className="card-title mb-0 fs-6">
                                                                {video.product_title}
                                                            </h5>
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Pagination */}
                                <div className="d-flex justify-content-center align-items-center">
                                    <div className="pagination-area" style={{ marginBottom: "30px" }}>
                                        <nav>
                                            <ul className="pagination justify-content-start">
                                                {/* Previous Button */}
                                                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(page - 1)}
                                                    >
                                                        <i className="fi-rs-arrow-small-left" />
                                                    </button>
                                                </li>

                                                {/* Page Numbers */}
                                                {[...Array(totalPages)].map((_, i) => {
                                                    const pageNumber = i + 1;
                                                    if (
                                                        pageNumber === page || // Current
                                                        pageNumber === page - 1 || // Before
                                                        pageNumber === page + 1 // After
                                                    ) {
                                                        return (
                                                            <li
                                                                key={pageNumber}
                                                                className={`page-item ${page === pageNumber ? "active" : ""}`}
                                                            >
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(pageNumber)}
                                                                >
                                                                    {pageNumber}
                                                                </button>
                                                            </li>
                                                        );
                                                    }
                                                    return null;
                                                })}

                                                {/* Next Button */}
                                                <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(page + 1)}
                                                    >
                                                        <i className="fi-rs-arrow-small-right" />
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>

            {/* Modal */}
            {activeVideo && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
                    onClick={handleCloseModal}
                >
                    <div
                        className="modal-dialog modal-dialog-centered modal-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0 rounded-4 overflow-hidden">
                            <div className="ratio ratio-16x9">
                                <iframe
                                    src={`https://www.youtube.com/embed/${getYouTubeId(activeVideo.product_video_url)}?autoplay=1`}
                                    title={activeVideo.product_title}
                                    allow="autoplay; encrypted-media"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Videos;
